/*
 * ============================================================
 * SAV_ERP — Smart Account Vision Enterprise Resource Planning
 * Copyright (c) 2026 Hatem Mohamed Abdalla.
 * All rights reserved.
 *
 * This file is part of a proprietary software product owned
 * solely by Hatem Mohamed Abdalla (UAE, +971 50 390 9781).
 * Copying, redistribution, modification, reverse engineering
 * or reuse in any form without prior written consent of the
 * copyright owner is strictly prohibited and will be
 * prosecuted under UAE Federal Law No. 38/2021 on Copyright
 * and applicable international treaties.
 *
 * Fingerprint: SAV-ERP-HMA-2026-#a7f3d0
 * ============================================================
 */
/**
 * Unified Error Handler Utility
 */

const formatDeletionBlockers = (payload, fallbackMessage = 'لا يمكن الحذف لوجود ارتباطات نشطة') => {
  if (!payload || typeof payload !== 'object') return fallbackMessage;

  const details = payload.details && typeof payload.details === 'object' ? payload.details : payload;
  const blockers = Array.isArray(details.blockers) ? details.blockers : [];
  const header = payload.message || details.message || fallbackMessage;

  if (!blockers.length) return header;

  const lines = blockers.map((item) => {
    const label = item?.label_ar || item?.label || item?.label_en || item?.key || 'ارتباطات';
    const count = Number(item?.count || 0);
    return `- ${label}: ${count}`;
  });

  const suggestion = details?.suggestion || payload?.suggestion;
  return [header, ...lines, suggestion].filter(Boolean).join('\n');
};

export const formatDeletionCheckMessage = (payload, fallbackMessage = 'تعذر تحديد سبب منع الحذف') => {
  return formatDeletionBlockers(payload, fallbackMessage);
};

export const extractErrorMessage = (error, fallbackMessage = 'حدث خطأ غير متوقع') => {
  if (!error) return fallbackMessage;
  if (typeof error === 'string') return error;

  if (error.response) {
    const data = error.response.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data?.message) return data.message;
    if (data?.error) return data.error;
    if (data?.detail) {
      if (typeof data.detail === 'string') return data.detail;
      if (Array.isArray(data.detail)) {
        return data.detail.map((item) => item?.msg || item?.message || JSON.stringify(item)).join(', ');
      }
      return formatDeletionBlockers(data.detail, fallbackMessage);
    }
    if (Array.isArray(data?.details)) {
      return data.details.map((item) => item?.msg || item?.message || JSON.stringify(item)).join(', ');
    }

    const statusMessages = {
      400: 'طلب غير صالح',
      401: 'غير مصرح - يرجى تسجيل الدخول',
      403: 'غير مسموح - ليس لديك صلاحية',
      404: 'غير موجود',
      409: 'تعارض في البيانات',
      422: 'بيانات غير صالحة',
      429: 'طلبات كثيرة - يرجى الانتظار',
      500: 'خطأ في الخادم',
      502: 'خطأ في البوابة',
      503: 'الخدمة غير متوفرة',
    };
    return statusMessages[error.response.status] || `خطأ HTTP ${error.response.status}`;
  }

  if (error.code === 'ECONNABORTED' || /timeout/i.test(String(error.message || ''))) {
    return 'انتهت مهلة انتظار المتصفح قبل رد الخادم. في R245 تم تمديد مهلة مسارات النسخ الثقيلة؛ لو ظهرت هذه الرسالة مرة أخرى افتح حالة النسخ أو سجل الخادم لمعرفة المرحلة الحالية.';
  }
  if (error.request) return 'تعذر إكمال الطلب أو تنزيل الملف. تحقق من اتصال الخادم ثم أعد المحاولة.';
  if (error.message) return error.message;
  return fallbackMessage;
};

export const extractTraceId = (error) => {
  const data = error?.response?.data;
  return data?.trace_id || data?.traceId || error?.response?.headers?.['x-trace-id'] || '';
};

export const logError = (arg1, arg2 = '') => {
  let error = arg1;
  let context = arg2;

  const looksLikeErrorObject =
    arg1 && typeof arg1 === 'object' && (
      arg1 instanceof Error ||
      'message' in arg1 ||
      'response' in arg1 ||
      'request' in arg1
    );

  if (!looksLikeErrorObject && (typeof arg1 === 'string' || typeof arg1 === 'number')) {
    context = String(arg1);
    error = arg2;
  }

  if (process.env.REACT_APP_ENABLE_ERROR_CONSOLE === 'true') {
    const title = context ? `🔴 Error: ${context}` : '🔴 Error';
    console.group(title);
    console.error('Error object:', error);
    if (error?.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
      const traceId = extractTraceId(error);
      if (traceId) console.error('Trace ID:', traceId);
    }
    console.groupEnd();
  }
};

export const handleApiError = (error, options = {}) => {
  const {
    showToast = true,
    toastFn = null,
    fallbackMessage = 'حدث خطأ غير متوقع',
    logContext = '',
  } = options;

  const message = extractErrorMessage(error, fallbackMessage);
  logError(error, logContext);

  if (showToast && toastFn) {
    toastFn({
      title: 'خطأ',
      description: message,
      variant: 'destructive',
    });
  }

  return message;
};

export const isTokenExpiredError = (error) => {
  if (error?.response?.status !== 401) return false;
  const detail = String(error.response?.data?.detail || error.message || '').toLowerCase();
  return detail.includes('expired') || detail.includes('invalid token') || detail.includes('not authenticated');
};

const errorHandler = {
  extractErrorMessage,
  logError,
  handleApiError,
  isTokenExpiredError,
  extractTraceId,
  formatDeletionCheckMessage,
};

export default errorHandler;
